// The sandbox playground: a browser stand-in for a game server, using a
// short-lived sandbox key, so developers can try every money movement.
import { errorMessage } from "../../lib/errors";
import { describeReversal } from "../../lib/reversal";
import { useMemo, useState } from "react";
import { Button, Confirm, CopyButton, Empty, ErrorNotice, Field, Input, Loading, Modal, Money, Panel, Segmented, Select, StatusBadge, useToast } from "../../components/ui";
import { ApiClient, ApiError, developerApi, gameApi, type GameApi } from "../../lib/api";
import { newIdempotencyKey } from "../../lib/ids";
import { formatMoney, parseMoney } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link } from "../../lib/router";
import { useClient, useSession } from "../../lib/session";
import { formatGameTime } from "../../lib/time";
import { ALL_SCOPES, type Account, type Bank, type HistoryItem, type Hold, type NameEnquiry } from "../../lib/types";
import type { GameTabProps } from "./Game";
import { CardPayments, CardsPanel, PayByCard } from "./SandboxCards";
import { ApplyLoan, CreditScorePanel, LoansPanel } from "./SandboxLoans";
import { FixedDepositsPanel, InterestPanel, OpenFixedDeposit } from "./SandboxSavings";

interface PlaygroundKey {
  id?: string;
  secret: string;
}

const storeKey = (gameId: string) => `gbe.sandboxKey.${gameId}`;
function loadKey(gameId: string): PlaygroundKey | null {
  try {
    const raw = sessionStorage.getItem(storeKey(gameId));
    return raw ? (JSON.parse(raw) as PlaygroundKey) : null;
  } catch {
    return null;
  }
}
function saveKey(gameId: string, k: PlaygroundKey | null) {
  try {
    if (k) sessionStorage.setItem(storeKey(gameId), JSON.stringify(k));
    else sessionStorage.removeItem(storeKey(gameId));
  } catch {
    /* memory only */
  }
}

export function Sandbox({ game }: GameTabProps) {
  const devClient = useClient();
  const { session } = useSession();
  const toast = useToast();
  const [key, setKey] = useState<PlaygroundKey | null>(() => loadKey(game.id));
  const [paste, setPaste] = useState("");
  const start = useAction(async () => {
    const r = await developerApi(devClient).issueKey(game.id, {
      environment: "sandbox",
      label: `Sandbox playground (browser, ${new Date().toLocaleDateString()})`,
      scopes: [...ALL_SCOPES],
    });
    return { id: r.api_key.id, secret: r.api_key.secret ?? "" };
  });
  const end = useAction(async () => {
    if (key?.id) await developerApi(devClient).revokeKey(game.id, key.id);
    return true;
  });

  const use = (k: PlaygroundKey) => {
    saveKey(game.id, k);
    setKey(k);
    invalidate(`dev:keys:${game.id}`);
  };

  const api = useMemo(() => (key && session ? gameApi(new ApiClient(session.baseUrl, key.secret)) : null), [key, session]);

  if (!key || !api) {
    return (
      <Panel title="Sandbox playground" sub="Act as your game server in the browser: open accounts, pay players, move money and read statements. Everything happens in sandbox.">
        <div className="grid-2">
          <div className="stack" style={{ gap: 12 }}>
            <h3>Start a playground session</h3>
            <p className="muted small">
              We'll issue a sandbox key with every scope, used only by this browser tab. Ending the session revokes it. It never works in production.
            </p>
            <ErrorNotice error={start.error} />
            <div>
              <Button
                variant="primary"
                busy={start.pending}
                onClick={async () => {
                  const k = await start.run();
                  if (k) {
                    use(k);
                    toast("Playground session started");
                  }
                }}
              >
                Start session
              </Button>
            </div>
          </div>
          <form
            className="stack"
            style={{ gap: 12 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (paste.trim().startsWith("gbe_test_sk_")) use({ secret: paste.trim() });
            }}
          >
            <h3>Or use one of your sandbox keys</h3>
            <Field label="Sandbox key" hint={paste && !paste.startsWith("gbe_test_sk_") ? "Only sandbox keys (gbe_test_sk_…) are accepted here." : "Useful to see exactly what a specific server key can do."}>
              <Input type="password" value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="gbe_test_sk_…" autoComplete="off" />
            </Field>
            <div><Button type="submit" disabled={!paste.trim().startsWith("gbe_test_sk_")}>Use this key</Button></div>
          </form>
        </div>
      </Panel>
    );
  }

  return (
    <Playground
      api={api}
      gameId={game.id}
      onEnd={async () => {
        if (await end.run()) {
          saveKey(game.id, null);
          setKey(null);
          invalidate(`dev:keys:${game.id}`);
          toast(key.id ? "Session ended and key revoked" : "Stopped using that key");
        }
      }}
      ending={end.pending}
      onKeyRejected={() => {
        saveKey(game.id, null);
        setKey(null);
      }}
    />
  );
}

function Playground({ api, gameId, onEnd, ending, onKeyRejected }: { api: GameApi; gameId: string; onEnd: () => void; ending: boolean; onKeyRejected: () => void }) {
  const banks = useQuery(`sandbox:${gameId}:banks`, () => api.banks());
  const clock = useQuery(`sandbox:${gameId}:clock`, () => api.clock(), { refreshMs: 10_000 });
  const [bankId, setBankId] = useState<string>("");
  const list = banks.data?.data ?? [];
  const bank = list.find((b) => b.id === bankId) ?? list[0];

  if (banks.error instanceof ApiError && banks.error.status === 401) {
    return (
      <Panel title="This playground key no longer works">
        <p className="muted" style={{ marginBottom: 12 }}>It was revoked or has expired. Start a new session to continue.</p>
        <Button variant="primary" onClick={onKeyRejected}>Start again</Button>
      </Panel>
    );
  }

  return (
    <div className="stack">
      <div className="panel" style={{ padding: "12px 16px" }}>
        <div className="row">
          <span className="badge sandbox">Sandbox session</span>
          {list.length > 0 && (
            <Select value={bank?.id ?? ""} onChange={(e) => setBankId(e.target.value)} aria-label="Bank" style={{ width: "auto" }}>
              {list.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.bank_code}, {b.base_currency.code})</option>)}
            </Select>
          )}
          {clock.data && (
            <span className="muted small">
              Game time {formatGameTime(clock.data.game_time, clock.data.time_zone)} ({clock.data.mode.replace("_", " ")})
            </span>
          )}
          {clock.data?.mode === "manual" && <QuickAdvance api={api} gameId={gameId} />}
          <span className="grow" />
          <Button size="small" busy={ending} onClick={onEnd}>End session</Button>
        </div>
      </div>
      <ErrorNotice error={banks.error} onRetry={banks.reload} />
      {banks.loading && !banks.data && <Loading />}
      {banks.data && list.length === 0 && (
        <Panel title="Create a bank first">
          <Empty title="This game has no sandbox bank yet" action={<Link className="btn primary" to={`/dev/games/${gameId}/banks`}>Create a sandbox bank</Link>}>
            Accounts live inside a bank.
          </Empty>
        </Panel>
      )}
      {bank && <BankFloor key={bank.id} api={api} gameId={gameId} bank={bank} />}
    </div>
  );
}

function QuickAdvance({ api, gameId }: { api: GameApi; gameId: string }) {
  const toast = useToast();
  const adv = useAction((body: { hours?: number; days?: number }) => api.advanceClock(body));
  const go = async (body: { hours?: number; days?: number }, label: string) => {
    const r = await adv.run(body);
    if (r) {
      invalidate(`sandbox:${gameId}`);
      toast(`Advanced ${label}${r.jobs.holds_expired ? `; ${r.jobs.holds_expired} hold(s) expired` : ""}`);
    } else if (adv.error) toast(errorMessage(adv.error), "error");
  };
  return (
    <span className="row" style={{ gap: 6 }}>
      <Button size="small" busy={adv.pending} onClick={() => go({ hours: 1 }, "1 hour")}>+1 hour</Button>
      <Button size="small" busy={adv.pending} onClick={() => go({ days: 1 }, "1 day")}>+1 day</Button>
      <Button size="small" busy={adv.pending} onClick={() => go({ days: 7 }, "7 days")} title="A week: weekly loan instalments are collected">+7 days</Button>
      <Button size="small" busy={adv.pending} onClick={() => go({ days: 30 }, "30 days")} title="A month: savings interest is paid">+30 days</Button>
    </span>
  );
}

type Action = "open" | "fund" | "send" | "hold" | "card" | "fd" | "loan" | null;

function BankFloor({ api, gameId, bank }: { api: GameApi; gameId: string; bank: Bank }) {
  const accounts = useQuery(`sandbox:${gameId}:accounts:${bank.id}`, () => api.accounts({ bank_id: bank.id, limit: 200 }));
  const [selectedId, setSelectedId] = useState<string>("");
  const [action, setAction] = useState<Action>(null);
  const rows = accounts.data?.data ?? [];
  const selected = rows.find((a) => a.id === selectedId) ?? rows[0];
  const shops = rows.filter((a) => a.owner_type === "merchant" && a.status !== "closed");
  const cardsOn = bank.feature_flags.cards === true;
  const isShop = selected?.owner_type === "merchant";
  const isCurrent = selected?.account_type === "current";
  const savingsOn = bank.feature_flags.savings === true;
  const hasInterest = !!selected?.product_id;
  const loansOn = bank.feature_flags.loans === true;
  const isLoan = selected?.account_type === "loan";
  const isPlayer = !!selected?.player_id;

  return (
    <div className="floor">
      <Panel title="Accounts" sub={`${rows.length - shops.length} player account${rows.length - shops.length === 1 ? "" : "s"}${shops.length ? ` and ${shops.length} shop${shops.length === 1 ? "" : "s"}` : ""} in ${bank.name}`} actions={<Button variant="primary" size="small" onClick={() => setAction("open")}>Open account</Button>} flush>
        <ErrorNotice error={accounts.error} onRetry={accounts.reload} />
        {accounts.loading && !accounts.data && <Loading />}
        {accounts.data && rows.length === 0 && (
          <Empty title="No accounts yet" action={<Button variant="primary" onClick={() => setAction("open")}>Open the first account</Button>}>
            Open two so you can try a transfer.
          </Empty>
        )}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Account</th><th className="num">Available</th></tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className="clickable" onClick={() => setSelectedId(a.id)} aria-selected={selected?.id === a.id}
                    style={selected?.id === a.id ? { boxShadow: "inset 3px 0 0 var(--role)", background: "var(--surface-2)" } : undefined}>
                    <td>
                      <div className="cell-title">
                        {a.account_name}
                        {a.owner_type === "merchant" && <span className="badge plain" style={{ marginLeft: 6 }}>Shop</span>}
                        {a.account_type === "savings" && <span className="badge plain" style={{ marginLeft: 6 }}>Savings</span>}
                        {a.account_type === "fixed_deposit" && <span className="badge plain" style={{ marginLeft: 6 }}>Fixed deposit</span>}
                        {a.account_type === "loan" && <span className="badge plain" style={{ marginLeft: 6 }}>Loan</span>}
                      </div>
                      <div className="cell-sub"><span className="acct-no">{a.account_number}</span> {a.status !== "active" && <StatusBadge status={a.status} />}</div>
                    </td>
                    <td className="num">
                      <Money minor={a.available_balance} currency={a.currency} />
                      {a.held_balance > 0 && <div className="cell-sub">{formatMoney(a.held_balance, a.currency)} held</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <div className="stack">
        {selected ? (
          <>
            <div className="row">
              {!isShop && !isLoan && selected.account_type !== "fixed_deposit" && <Button variant="primary" onClick={() => setAction("fund")}>Pay this player</Button>}
              {!isShop && isCurrent && loansOn && <Button onClick={() => setAction("loan")} disabled={selected.status !== "active"}>Borrow</Button>}
              {!isShop && isCurrent && cardsOn && <Button onClick={() => setAction("card")} disabled={selected.status !== "active"}>Pay by card</Button>}
              {!isShop && !isLoan && selected.account_type !== "fixed_deposit" && savingsOn && <Button onClick={() => setAction("fd")} disabled={selected.status !== "active"}>Fixed deposit</Button>}
              {selected.account_type !== "fixed_deposit" && !isLoan && <Button onClick={() => setAction("send")} disabled={selected.status !== "active"}>Send money</Button>}
              {isCurrent && <Button onClick={() => setAction("hold")} disabled={selected.status !== "active"}>Place a hold</Button>}
              {!isLoan && <FreezeToggle api={api} gameId={gameId} account={selected} />}
            </div>
            <Passbook api={api} gameId={gameId} account={selected} bank={bank} />
            <Holds api={api} gameId={gameId} account={selected} />
            {hasInterest && <InterestPanel key={`i-${selected.id}`} api={api} gameId={gameId} account={selected} />}
            {savingsOn && !isShop && <FixedDepositsPanel key={`f-${selected.id}`} api={api} gameId={gameId} account={selected} />}
            {isCurrent && !isShop && <LoansPanel key={`l-${selected.id}`} api={api} gameId={gameId} account={selected} />}
            {isPlayer && !isLoan && (loansOn || isCurrent) && <CreditScorePanel key={`s-${selected.id}`} api={api} gameId={gameId} account={selected} />}
            {!isShop && isCurrent && <CardsPanel key={selected.id} api={api} gameId={gameId} account={selected} bank={bank} cardsOn={cardsOn} />}
            <CardPayments key={`p-${selected.id}`} api={api} gameId={gameId} account={selected} cardsOn={cardsOn} />
          </>
        ) : (
          <Panel title="Statement"><p className="muted">Open an account to see its statement here.</p></Panel>
        )}
      </div>
      <OpenAccount open={action === "open"} onClose={() => setAction(null)} api={api} gameId={gameId} bank={bank} onOpened={(id) => setSelectedId(id)} />
      {selected && (
        <>
          <Fund open={action === "fund"} onClose={() => setAction(null)} api={api} gameId={gameId} account={selected} />
          <Send open={action === "send"} onClose={() => setAction(null)} api={api} gameId={gameId} account={selected} bank={bank} others={rows.filter((r) => r.id !== selected.id)} />
          {action === "loan" && <ApplyLoan onClose={() => setAction(null)} api={api} gameId={gameId} account={selected} bank={bank} />}
          {action === "fd" && <OpenFixedDeposit onClose={() => setAction(null)} api={api} gameId={gameId} account={selected} bank={bank} />}
          {action === "card" && <PayByCard open onClose={() => setAction(null)} api={api} gameId={gameId} account={selected} shops={shops} />}
          <PlaceHold open={action === "hold"} onClose={() => setAction(null)} api={api} gameId={gameId} account={selected} others={rows.filter((r) => r.id !== selected.id)} />
        </>
      )}
    </div>
  );
}

// ---------- statement ----------

function Passbook({ api, gameId, account, bank }: { api: GameApi; gameId: string; account: Account; bank: Bank }) {
  const toast = useToast();
  const hist = useQuery(`sandbox:${gameId}:history:${account.id}`, () => api.history(account.id, { limit: 50 }));
  const [reversing, setReversing] = useState<HistoryItem | null>(null);
  const [reason, setReason] = useState("");
  const [revKey, setRevKey] = useState(newIdempotencyKey("reverse"));
  const reverse = useAction((h: HistoryItem) => api.reverse(h.transaction_id, reason.trim(), revKey));
  // Load the whole transaction so the confirmation states everything the reversal moves.
  const revTx = useQuery(reversing ? `sandbox:${gameId}:tx:${reversing.transaction_id}` : null, () => api.transaction(reversing!.transaction_id));
  const refundFees = bank.feature_flags.refund_fees_on_reversal !== false;
  const isFeeCharge = reversing?.type === "fee";
  const brand = String(bank.branding.primary_color ?? "");

  return (
    <section className="passbook" aria-label={`Statement for ${account.account_name}`}>
      <div className="passbook-head" style={brand ? { background: brand, color: "#fff" } : undefined}>
        <div>
          <div style={{ fontWeight: 600 }}>{account.account_name}</div>
          <div className="row" style={{ gap: 8 }}>
            <span className="acct-no">{account.account_number}</span>
            <span className="small" style={{ opacity: 0.85 }}>{bank.name}, code {bank.bank_code}</span>
          </div>
        </div>
        <div className="bal">
          <span className="small" style={{ opacity: 0.85 }}>Available</span>
          <strong>{formatMoney(account.available_balance, account.currency)}</strong>
          <span className="small" style={{ opacity: 0.85 }}>Ledger balance {formatMoney(account.posted_balance, account.currency)}</span>
        </div>
      </div>
      <ErrorNotice error={hist.error} onRetry={hist.reload} />
      {hist.data && hist.data.data.length === 0 && <p className="muted" style={{ padding: 18 }}>No transactions yet. {account.owner_type === "merchant" ? "Take a card payment or send money to this shop to see the first line." : "Pay this player to see the first line."}</p>}
      {hist.data && hist.data.data.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Game time</th>
                <th>Details</th>
                <th className="num">Debit</th>
                <th className="num">Credit</th>
                <th className="num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {hist.data.data.map((h) => (
                <tr key={h.entry_id}>
                  <td style={{ whiteSpace: "nowrap" }}>{formatGameTime(h.game_time).replace(/ \d{4},/, ",")}</td>
                  <td>
                    <div>{h.narration ?? h.type}</div>
                    <div className="cell-sub">
                      {h.counterparty ? `${h.direction === "debit" ? "To" : "From"} ${h.counterparty.account_name}` : ""}
                      {h.status === "reversed" && <> <StatusBadge status="reversed" /></>}
                    </div>
                    <div className="cell-sub row" style={{ gap: 6 }}>
                      <span className="mono truncate" style={{ maxWidth: 210 }} title={`Session ID ${h.session_id}`}>{h.session_id}</span>
                      {/* A reversal undoes the whole transaction, so offer it on the payment's own line, not on its fee lines.
                          A standalone fee charge (e.g. monthly maintenance) is refunded instead. Card payments are refunded by the shop. */}
                      {h.status === "posted" && h.type !== "reversal" && h.type !== "card_payment" && h.type !== "card_refund" && h.type !== "interest" &&
                        ((h.kind ?? "principal") === "principal" || h.type === "fee") && (
                        <button className="btn ghost small" style={{ padding: "0 4px" }} onClick={() => { setReversing(h); setReason(""); setRevKey(newIdempotencyKey("reverse")); }}>
                          {h.type === "fee" ? "Refund fee" : "Reverse"}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="num">{h.direction === "debit" && <Money minor={h.amount_minor} currency={account.currency} tone="debit" />}</td>
                  <td className="num">{h.direction === "credit" && <Money minor={h.amount_minor} currency={account.currency} tone="credit" />}</td>
                  <td className="num">{formatMoney(h.balance_after, account.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Confirm
        open={!!reversing}
        title={isFeeCharge ? "Refund this fee?" : "Reverse this transaction?"}
        body={revTx.data
          ? <>{describeReversal(revTx.data, refundFees, account.currency)} The original stays on the statement, marked reversed.</>
          : revTx.error ? <>The whole transaction is reversed, including any fees charged with it.</> : <>Working out what this reverses…</>}
        confirmLabel={isFeeCharge ? "Refund fee" : "Reverse transaction"}
        danger
        busy={reverse.pending}
        error={reverse.error}
        onConfirm={async () => {
          if (reversing && reason.trim() && (await reverse.run(reversing))) {
            setReversing(null);
            invalidate(`sandbox:${gameId}`);
            toast(isFeeCharge ? "Fee refunded" : "Reversed");
          }
        }}
        onClose={() => setReversing(null)}
      >
        <Field label="Reason"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Dispute upheld" required /></Field>
      </Confirm>
    </section>
  );
}

function Holds({ api, gameId, account }: { api: GameApi; gameId: string; account: Account }) {
  const toast = useToast();
  const q = useQuery(`sandbox:${gameId}:holds:${account.id}`, () => api.holds(account.id, "active"));
  const [capturing, setCapturing] = useState<Hold | null>(null);
  const [amount, setAmount] = useState("");
  const cap = useAction((h: Hold, minor?: number) => api.captureHold(h.id, { amount_minor: minor }, newIdempotencyKey("capture")));
  const rel = useAction((h: Hold) => api.releaseHold(h.id, newIdempotencyKey("release")));
  const holds = q.data?.data ?? [];
  if (holds.length === 0) return null;
  return (
    <Panel title="Active holds" sub="Reserved funds. Capture to pay, release to give back. Uncaptured holds expire on the game clock." flush>
      <ErrorNotice error={rel.error} />
      <table className="data">
        <tbody>
          {holds.map((h) => (
            <tr key={h.id}>
              <td>
                <div className="cell-title">{h.reason}</div>
                <div className="cell-sub">Expires {formatGameTime(h.expires_at_game)} game time</div>
              </td>
              <td className="num"><Money minor={h.amount_minor} currency={account.currency} /></td>
              <td className="num">
                <span className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                  <Button size="small" disabled={!h.destination_account_id} title={h.destination_account_id ? undefined : "Placed without a recipient"} onClick={() => { setCapturing(h); setAmount(""); }}>Capture</Button>
                  <Button size="small" busy={rel.pending} onClick={async () => { if (await rel.run(h)) { invalidate(`sandbox:${gameId}`); toast("Hold released"); } }}>Release</Button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Confirm
        open={!!capturing}
        title="Capture this hold"
        body={<>Pays the recipient and releases whatever you don't capture.</>}
        confirmLabel="Capture"
        busy={cap.pending}
        error={cap.error}
        onConfirm={async () => {
          if (!capturing) return;
          const minor = amount.trim() ? parseMoney(amount, account.currency.exponent) : undefined;
          if (minor === null) return;
          const res = await cap.run(capturing, minor);
          if (res) {
            setCapturing(null);
            invalidate(`sandbox:${gameId}`);
            toast(res.fees_total_minor > 0
              ? `Hold captured; fees ${formatMoney(res.fees_total_minor, res.transaction.currency)}`
              : "Hold captured");
          }
        }}
        onClose={() => setCapturing(null)}
      >
        <Field label={`Amount (${account.currency.code})`} hint={capturing ? `Leave empty to capture all ${formatMoney(capturing.amount_minor, account.currency)}.` : undefined}>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
      </Confirm>
    </Panel>
  );
}

function FreezeToggle({ api, gameId, account }: { api: GameApi; gameId: string; account: Account }) {
  const toast = useToast();
  const act = useAction((s: string) => api.setAccountStatus(account.id, s, "sandbox test"));
  if (account.status === "closed") return null;
  const next = account.status === "frozen" ? "active" : "frozen";
  return (
    <Button
      busy={act.pending}
      onClick={async () => {
        if (await act.run(next)) {
          invalidate(`sandbox:${gameId}`);
          toast(next === "frozen" ? "Account frozen: it can receive but not send" : "Account unfrozen");
        } else if (act.error) toast(errorMessage(act.error), "error");
      }}
    >
      {account.status === "frozen" ? "Unfreeze" : "Freeze"}
    </Button>
  );
}

// ---------- action dialogs ----------

function useOpKey(open: boolean) {
  // One idempotency key per dialog opening: retries of the same submission reuse it.
  return useMemo(() => newIdempotencyKey(), [open]);
}

function AmountField({ value, onChange, account, label = "Amount" }: { value: string; onChange: (v: string) => void; account: Account; label?: string }) {
  const minor = parseMoney(value, account.currency.exponent);
  return (
    <Field label={`${label} (${account.currency.code})`} hint={value && minor === null ? `Use a number with at most ${account.currency.exponent} decimal places.` : minor ? `${minor.toLocaleString()} minor units` : undefined}>
      <Input inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} required aria-invalid={!!value && minor === null} placeholder={account.currency.exponent ? "1,000.00" : "1000"} />
    </Field>
  );
}

function OpenAccount({ open, onClose, api, gameId, bank, onOpened }: { open: boolean; onClose: () => void; api: GameApi; gameId: string; bank: Bank; onOpened: (id: string) => void }) {
  const toast = useToast();
  const [kind, setKind] = useState<"player" | "shop" | "savings">("player");
  const [productId, setProductId] = useState("");
  const products = useQuery(open && bank.feature_flags.savings ? `sandbox:${gameId}:deposit-products:${bank.id}` : null, () => api.depositProducts(bank.id));
  const savingsProducts = (products.data?.data ?? []).filter((p) => p.kind === "savings" && p.status === "active");
  const product = savingsProducts.find((p) => p.id === productId) ?? savingsProducts[0];
  const [name, setName] = useState("");
  const [ext, setExt] = useState("");
  const [category, setCategory] = useState("general");
  const act = useAction(async () => {
    if (kind === "shop") {
      const m = await api.createMerchant({ external_merchant_id: ext.trim() || `shop-${Date.now().toString(36)}`, name: name.trim(), category: category.trim() || "general" });
      return api.openAccount({ bank_id: bank.id, merchant_id: m.id });
    }
    const p = await api.upsertPlayer({ external_player_id: ext.trim() || `player-${Date.now().toString(36)}`, display_name: name.trim() });
    if (kind === "savings") {
      if (!product) throw new Error("Add a savings product in the Savings tab first.");
      return api.openAccount({ bank_id: bank.id, player_id: p.id, product_id: product.id });
    }
    return api.openAccount({ bank_id: bank.id, player_id: p.id });
  });
  const shop = kind === "shop";
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Open an account"
      description={shop ? "Registers a shop (a merchant) and opens its merchant account. Card payments settle into it."
        : kind === "savings" ? "Maps a player from your game and opens a savings account that earns interest under the product you pick."
        : "Maps a player from your game and opens a current account for them."}
      onSubmit={async () => {
        const a = await act.run();
        if (a) {
          invalidate(`sandbox:${gameId}:accounts`);
          onOpened(a.id);
          toast(`Opened ${a.account_number} for ${a.account_name}`);
          setName("");
          setExt("");
          onClose();
        }
      }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={act.pending}>Open account</Button></>}
    >
      <div className="form">
        <Segmented<"player" | "shop" | "savings"> label="Account for" value={kind} onChange={setKind}
          options={[{ value: "player", label: "Player" }, ...(bank.feature_flags.savings ? [{ value: "savings" as const, label: "Player savings" }] : []), { value: "shop", label: "Shop (merchant)" }]} />
        {kind === "savings" && (savingsProducts.length > 0 ? (
          <Field label="Savings product" hint="Reusing a player ID gives an existing player a savings account.">
            <Select value={product?.id} onChange={(e) => setProductId(e.target.value)}>{savingsProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
          </Field>
        ) : products.data && <div className="notice">Add a savings product in the Savings tab first.</div>)}
        <Field label={shop ? "Shop name" : "Player name"}><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={shop ? "Mama Put" : "Ada Obi"} required autoFocus /></Field>
        {shop && (
          <Field label="Category" hint="Lowercase, e.g. food, weapons, clothing.">
            <Input value={category} onChange={(e) => setCategory(e.target.value.toLowerCase())} pattern="[a-z][a-z0-9_]{1,39}" />
          </Field>
        )}
        <Field label={shop ? "Shop ID in your game" : "Player ID in your game"} hint={shop ? "Optional. Reusing an ID opens another account for the same shop." : "Optional. Reusing an ID opens another account for the same player."}>
          <Input value={ext} onChange={(e) => setExt(e.target.value)} placeholder={shop ? "shop:mama-put" : "steam:7001"} />
        </Field>
        <ErrorNotice error={act.error} />
      </div>
    </Modal>
  );
}

function Fund({ open, onClose, api, gameId, account }: { open: boolean; onClose: () => void; api: GameApi; gameId: string; account: Account }) {
  const toast = useToast();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("salary");
  const [narration, setNarration] = useState("");
  const opKey = useOpKey(open);
  const act = useAction(() => {
    const minor = parseMoney(amount, account.currency.exponent);
    if (!minor) throw new Error("Enter an amount greater than zero.");
    return api.fund({ account_id: account.id, amount_minor: minor, reason, reference: opKey, narration: narration.trim() || undefined }, opKey);
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Pay ${account.account_name}`}
      description="Funding creates money: it debits the bank's issuance account and credits the player. Limits apply."
      onSubmit={async () => {
        const r = await act.run();
        if (r) {
          invalidate(`sandbox:${gameId}`);
          toast(`Paid ${r.transaction.amount_display}`);
          setAmount("");
          setNarration("");
          onClose();
        }
      }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={act.pending}>Pay player</Button></>}
    >
      <div className="form">
        <AmountField value={amount} onChange={setAmount} account={account} />
        <Field label="Reason">
          <Select value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="salary">Salary</option>
            <option value="quest_reward">Quest reward</option>
            <option value="bonus">Bonus</option>
            <option value="starter_grant">Starter grant</option>
          </Select>
        </Field>
        <Field label="Narration" hint="Shown on the player's statement."><Input value={narration} onChange={(e) => setNarration(e.target.value)} maxLength={140} placeholder="Salary: Mechanic, week 1" /></Field>
        <ErrorNotice error={act.error} />
      </div>
    </Modal>
  );
}

function Recipient({ api, bank, others, value, onChange, confirmed, setConfirmed }: {
  api: GameApi; bank: Bank; others: Account[]; value: string; onChange: (v: string) => void;
  confirmed: NameEnquiry | null; setConfirmed: (n: NameEnquiry | null) => void;
}) {
  const lookup = useAction(() => api.nameEnquiry(bank.bank_code, value.trim()));
  return (
    <div className="form">
      <div className="row end">
        <div className="grow">
          <Field label="Recipient account number">
            <Input className="input acct-no" inputMode="numeric" value={value} onChange={(e) => { onChange(e.target.value.replace(/\D/g, "").slice(0, 10)); setConfirmed(null); }} placeholder="10 digits" required />
          </Field>
        </div>
        <Button busy={lookup.pending} disabled={value.length !== 10} onClick={async () => { const r = await lookup.run(); setConfirmed(r ?? null); }}>Check name</Button>
      </div>
      {others.length > 0 && (
        <div className="row small" style={{ gap: 6 }}>
          <span className="muted">Quick pick:</span>
          {others.slice(0, 4).map((o) => (
            <button key={o.id} type="button" className="btn ghost small" onClick={() => { onChange(o.account_number); setConfirmed(null); }}>{o.account_name}</button>
          ))}
        </div>
      )}
      {confirmed && (
        <div className="notice role">
          Sending to <strong>{confirmed.account_name}</strong> at {confirmed.bank_name}. {confirmed.status !== "active" && <StatusBadge status={confirmed.status} />}
        </div>
      )}
      <ErrorNotice error={lookup.error} />
    </div>
  );
}

function Send({ open, onClose, api, gameId, account, bank, others }: { open: boolean; onClose: () => void; api: GameApi; gameId: string; account: Account; bank: Bank; others: Account[] }) {
  const toast = useToast();
  const [to, setTo] = useState("");
  const [confirmed, setConfirmed] = useState<NameEnquiry | null>(null);
  const [amount, setAmount] = useState("");
  const [narration, setNarration] = useState("");
  const opKey = useOpKey(open);
  // Quote the bank's fees for this amount, as a real banking app shows them before you confirm.
  const minorAmount = parseMoney(amount, account.currency.exponent);
  const quote = useQuery(open && minorAmount ? `sandbox:${gameId}:quote:${bank.id}:${minorAmount}` : null,
    () => api.quoteFees({ bank_id: bank.id, event: "transfer", amount_minor: minorAmount! }));
  const act = useAction(() => {
    const minor = parseMoney(amount, account.currency.exponent);
    if (!minor) throw new Error("Enter an amount greater than zero.");
    // Never charge more than the fee the player was shown.
    const maxFee = quote.data?.amount_minor === minor ? quote.data.fees_total_minor : undefined;
    return api.transfer({ source_account_id: account.id, destination_account_number: to, amount_minor: minor, narration: narration.trim() || undefined, max_fee_minor: maxFee }, opKey);
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Send money from ${account.account_name}`}
      description={`Available: ${formatMoney(account.available_balance, account.currency)}. Confirm the recipient's name before sending, as a real banking app would.`}
      onSubmit={async () => {
        const r = await act.run();
        if (r) {
          invalidate(`sandbox:${gameId}`);
          toast(r.fees_total_minor > 0
            ? `Sent ${r.transaction.amount_display} + ${formatMoney(r.fees_total_minor, r.transaction.currency)} in fees`
            : `Sent ${r.transaction.amount_display}`);
          setTo(""); setAmount(""); setNarration(""); setConfirmed(null);
          onClose();
        }
      }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={act.pending} disabled={!confirmed}>Send money</Button></>}
    >
      <div className="form">
        <Recipient api={api} bank={bank} others={others} value={to} onChange={setTo} confirmed={confirmed} setConfirmed={setConfirmed} />
        <AmountField value={amount} onChange={setAmount} account={account} />
        {quote.data && quote.data.amount_minor === minorAmount && (
          <div className="notice">
            {quote.data.fees.length === 0 ? "No fees on this transfer." : (
              <>
                {quote.data.fees.map((c) => <div key={c.fee_rule_id}>{c.name}{c.kind === "levy" ? " (levy)" : ""}: {formatMoney(c.amount_minor, account.currency)}</div>)}
                <div><strong>Total from {account.account_name}: {formatMoney(quote.data.total_debit_minor, account.currency)}</strong></div>
              </>
            )}
          </div>
        )}
        <Field label="Narration"><Input value={narration} onChange={(e) => setNarration(e.target.value)} maxLength={140} placeholder="Generator purchase" /></Field>
        <ErrorNotice error={act.error} />
        <p className="cell-sub">Idempotency key for this send: <span className="mono">{opKey}</span> <CopyButton text={opKey} /></p>
      </div>
    </Modal>
  );
}

function PlaceHold({ open, onClose, api, gameId, account, others }: { open: boolean; onClose: () => void; api: GameApi; gameId: string; account: Account; others: Account[] }) {
  const toast = useToast();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [to, setTo] = useState("");
  const [hours, setHours] = useState(24);
  const opKey = useOpKey(open);
  const act = useAction(() => {
    const minor = parseMoney(amount, account.currency.exponent);
    if (!minor) throw new Error("Enter an amount greater than zero.");
    return api.placeHold({ account_id: account.id, amount_minor: minor, reason: reason.trim(), destination_account_number: to || undefined, expires_in_game_seconds: Math.max(1, hours) * 3600 }, opKey);
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Place a hold"
      description="Reserves money now without moving it, like a card pre-authorisation. The available balance drops; the ledger balance doesn't."
      onSubmit={async () => {
        const r = await act.run();
        if (r) {
          invalidate(`sandbox:${gameId}`);
          toast("Hold placed");
          setAmount(""); setReason(""); setTo("");
          onClose();
        }
      }}
      footer={<><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="primary" busy={act.pending}>Place hold</Button></>}
    >
      <div className="form">
        <AmountField value={amount} onChange={setAmount} account={account} />
        <Field label="Reason"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Order #1042" required maxLength={200} /></Field>
        <Field label="Recipient account number" hint="Needed to capture the hold later. Leave empty for a pre-authorisation you'll only release.">
          <Select value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">No recipient</option>
            {others.map((o) => <option key={o.id} value={o.account_number}>{o.account_name} ({o.account_number})</option>)}
          </Select>
        </Field>
        <Field label="Expires after (game hours)"><Input type="number" min={1} value={hours} onChange={(e) => setHours(Number(e.target.value))} /></Field>
        <ErrorNotice error={act.error} />
      </div>
    </Modal>
  );
}
